import { produce } from 'immer';
import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  type ReactNode,
} from 'react';
import { useLoaderData } from 'react-router-dom';

import { getTotalPages } from 'src/core/utils/paginationUtil';
import { useAuthStatePageReload } from 'src/hooks/useAuthPageReload/useAuthPageReload';
import type {
  ThreadCard,
  FeaturedThreadsResult,
} from 'src/mapper/threadMapper';
import type { CommunityHomeLoaderData } from 'src/pages/CommunityHome';

interface ThreadsState {
  displayThreads: ThreadCard[];
  totalPage: number;
}

type ThreadsAction =
  | {
      type: 'UPDATE_THREADS';
      payload: { threads: ThreadCard[] };
    }
  | {
      type: 'UPDATE_THREAD_LIKE_IN_FEED';
      payload: { id: string; liked: boolean; likeCountLabel: string };
    };

const ThreadsContext = createContext<{
  threadsState: ThreadsState;
  threadsDispatch: React.Dispatch<ThreadsAction>;
} | null>(null);

const threadsReducer = (
  threadsState: ThreadsState,
  action: ThreadsAction,
): ThreadsState => {
  switch (action.type) {
    case 'UPDATE_THREADS':
      return {
        ...threadsState,
        displayThreads: action.payload.threads,
      };
    case 'UPDATE_THREAD_LIKE_IN_FEED':
      return produce(threadsState, (draft) => {
        const thread = draft.displayThreads.find(
          ({ id }) => id === action.payload.id,
        );
        if (thread) {
          thread.liked = action.payload.liked;
          thread.exactSocialData.likeCountLabel = action.payload.likeCountLabel;
        }
      });
    default:
      return threadsState;
  }
};

interface ThreadsProviderProps {
  children: ReactNode;
  initialData: FeaturedThreadsResult;
}

const useRefreshThreads = (
  threadsDispatch: React.Dispatch<ThreadsAction>,
  newThreads: FeaturedThreadsResult,
) => {
  useEffect(() => {
    threadsDispatch({
      type: 'UPDATE_THREADS',
      payload: { threads: newThreads.threads },
    });
  }, [newThreads, threadsDispatch]);
};

export const ThreadsProvider = ({
  children,
  initialData,
}: ThreadsProviderProps) => {
  const { init, isAuthenticated } = useLoaderData() as CommunityHomeLoaderData;
  const [threadsState, threadsDispatch] = useReducer(threadsReducer, {
    displayThreads: initialData.threads,
    totalPage: getTotalPages(initialData.totalThread),
  });

  useAuthStatePageReload(init, isAuthenticated);
  useRefreshThreads(threadsDispatch, initialData);

  return (
    <ThreadsContext.Provider
      value={{
        threadsState,
        threadsDispatch,
      }}
    >
      {children}
    </ThreadsContext.Provider>
  );
};

export const useThreads = () => {
  const context = useContext(ThreadsContext);
  if (!context) {
    throw new Error('useThreads must be used within a ThreadsProvider');
  }
  return context;
};
